import { Typography } from '@mui/material';
import { useState } from 'react';

import { PieceAction, PieceAssetSize, PieceAssetType } from '@apiContract';

import { apiRequest } from '@src/api/apiRequest';
import { uploadImageToS3 } from '@src/api/imageS3';
import { getUploadUrl } from '@src/api/piece';

import { useFinishPieceMutation } from '../../hooks/useFinishPieceMutation';
import { ActionComponentProps } from '../../index';
import { DialogSignatureConfirmation } from '../DialogSignatureConfirmation';
import { ActionWithInfo } from './ActionWithInfo';

type FinishWithSignatureProps = ActionComponentProps & {
  action: PieceAction.FinishDeliveryWithSignature | PieceAction.FinishPickUpWithSignature;
};

export const FinishWithSignature = ({
  mailpieces,
  actionStarted,
  onActionFinish,
  validationData,
  action,
}: FinishWithSignatureProps) => {
  const { runAction, isLoading } = useFinishPieceMutation();
  const [isUploading, setIsUploading] = useState<boolean>(false);

  const confirmAction = async (
    imageBase64: string,
    skipNotifyRecipient: boolean,
    comment: string,
    notifyMailroom: boolean,
  ) => {
    try {
      setIsUploading(true);

      const { pieceAssets } = await getUploadUrl(mailpieces[0].id);

      const { url, key } =
        pieceAssets.find(
          (asset) =>
            asset.pieceAssetType === PieceAssetType.PickupSignature && asset.pieceAssetSize === PieceAssetSize.Large,
        ) || {};

      await apiRequest(uploadImageToS3, { image: imageBase64, uploadUrl: url, contentType: 'image/jpg' }, false, 5);

      await runAction({
        action,
        pieces: mailpieces,
        comment,
        notifyMailroom,
        skipNotifyRecipient,
        validationData,
        additionalData: { pickupSignatureKey: key },
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <ActionWithInfo
      open={actionStarted}
      title="Complete with signature"
      onClose={onActionFinish}
      validationData={validationData}
      acceptButtonTitle="Capture Signature"
      infoContent={
        <Typography variant="body1" color="text.secondary">
          {mailpieces.length === 1 ? (
            <>Please ask the recipient to sign for this item.</>
          ) : (
            <>
              Please ask the recipient to sign for <strong>{mailpieces.length}</strong> items.
            </>
          )}
        </Typography>
      }
    >
      {({ step, skipNotifyRecipient, comment, notifyMailroom, onClose, onFinish }) => (
        <DialogSignatureConfirmation
          mailpieces={mailpieces}
          actionStarted={actionStarted && step === 'action'}
          persist={isUploading || isLoading}
          onActionFinish={onClose}
          onConfirm={async (imageBase64) => {
            await confirmAction(imageBase64, skipNotifyRecipient, comment, notifyMailroom);
            onFinish();
          }}
        />
      )}
    </ActionWithInfo>
  );
};